/**
 * assets/upgrade-finance.js
 * Upgrade financing widget behaviour: cash-on-delivery / pay-in-full summary
 * plus an illustrative instalment estimator. Mounts into any element
 * matching "#upgrade-finance-slot, [data-ug-finance-root]" -- that markup is
 * rendered by snippets/upgrade-finance.liquid.
 *
 * Author: Sara Abdelnour, Payments and Checkout Engineer.
 *
 * INTEGRATION CONTRACT for whoever wires the PDP buy-box (grade/storage
 * selector) to this widget -- that script is not owned by this file:
 *
 *   - data-price on the root element MUST be a plain EGP number as a
 *     string, e.g. "29099", NOT Shopify's cents-based money format (which
 *     would be "2909900"). 28 Sep 2026 (D11, promotions-and-partners spec):
 *     this file no longer guesses. It used to divide by 100 if it saw a
 *     number over 200,000 on THIS attribute, as a defensive fallback -- but
 *     the two real senders of data-price, assets/upgrade-pdp.js's buy-box
 *     (:1698, its figure taken from window.UpgradePrices) and this snippet's
 *     own `price` parameter (snippets/upgrade-finance.liquid; no current
 *     caller passes one), always send plain EGP, and the guess itself was a
 *     trap: raising MAX_PRICE (below) anywhere near 200,000 would have made a
 *     genuinely expensive price sent through data-price get silently divided
 *     by 100. Send plain EGP; a value that is not will be validated and
 *     rejected where it is typed, never rescaled. The removed heuristic only
 *     ever read the data-price ATTRIBUTE; it never touched the standalone
 *     calculator's manual price input, which is validated against
 *     MIN_PRICE/MAX_PRICE instead and rejected with an inline message rather
 *     than rescaled -- see the note below on where that input actually
 *     lives.
 *   - data-grade on the root element should be the grade's display label
 *     ("Gold" / "Platinum" / "Diamond") or a grade_code ("RG"/"RP"/"RD").
 *     Leave it as an empty string when no grade is selected yet.
 *   - Set data-price to an empty string (not "0") when no price should be
 *     shown yet (e.g. before the shopper picks a grade). This widget shows
 *     a "choose a grade" prompt instead of a wrong or stale number.
 *   - Update those two attributes directly on the mount element when the
 *     shopper changes grade/storage; this script watches them via
 *     MutationObserver and recomputes automatically. No page reload needed.
 *   - To force a recompute explicitly instead of relying on the observer,
 *     call window.UpgradeFinance.refresh(elementOrId).
 *
 * PAY MONTHLY DROPDOWN (28 Sep 2026): compact mode is the dropdown under the
 * buy buttons. Its closed row states the monthly figure, worked out exactly
 * as the How to pay calculator does (assets/upgrade-calc.js): price / months,
 * rounded UP to the pound, no fee, "before any fees" in the words. Those
 * words come from the markup (data-summary-template / data-summary-empty), so
 * this file adds none. A link to "#<this widget's id>" opens the dropdown.
 *
 * GATE: if data-grade resolves to something other than Gold, the whole
 * widget hides itself using the native `hidden` attribute (not
 * display:none) rather than showing a disabled/greyed state. This mirrors
 * the build spec's ug-financing component (Gold-only), a commercial
 * decision recorded in the pricing work, not here. Pass an empty/blank grade to skip the gate entirely -- used on
 * the standalone financing page, which explains the mechanism in general
 * rather than against one priced unit.
 */
(function () {
  'use strict';

  /* ---- ARABIC SLICE, 4 Oct 2026 (LOG-043; 2026-10-04_arabic/00-PLAN.md 7.3)
     Every customer word this file writes is in EN below, exactly as it was
     written inline before, read through T(). English pages have no
     dictionary, so T() returns EN's text and the English output is identical
     by construction (tests/finance-drop.test.js runs this file with no
     window.UpgradeI18n at all). On an Arabic page T() reads
     window.UpgradeI18n, falling back to EN per key. tools/build-i18n.js reads
     this table. The words of the "Pay monthly" row are NOT here: they come
     from the markup (data-summary-template / data-summary-empty), which is
     already translated by Liquid. The [monthly] / [months] / [term] tokens
     there are filled by fill() below; {name} placeholders here by tfill(). */
  var EN = {
    'common.money': '{amount} EGP',
    'finance.empty_full': 'Enter a price above ({range}) to see cash-on-delivery, card and instalment options for that price.',
    'finance.monthly_value': '{amount} / month',
    'finance.total_value': '{amount} total',
    'finance.extra_value': '{amount}, in this example',
    'finance.error_not_positive': 'Enter a number greater than 0.',
    'finance.error_min': 'Minimum price for this calculator is {amount}.',
    'finance.error_max': 'Maximum price for this calculator is {amount} – above anything in the Upgrade catalogue.',
    'finance.status': '{summary}.'
  };
  var I18N = (typeof window !== 'undefined' && window.UpgradeI18n) || null;
  // {name} -> value (this file's own fill() below handles [name] tokens).
  function tfill(str, vars) {
    return String(str == null ? '' : str).replace(/\{(\w+)\}/g, function (whole, name) {
      return (vars && Object.prototype.hasOwnProperty.call(vars, name) && vars[name] !== null && vars[name] !== undefined)
        ? String(vars[name]) : whole;
    });
  }
  // The dictionary's text when it has the key, else EN's. Never throws.
  function T(key, vars) {
    if (I18N) {
      try {
        if (I18N.has(key)) {
          var tr = I18N.t(key, vars);
          if (typeof tr === 'string') { return tr; }
        }
      } catch (e) { /* fall back to English */ }
    }
    return tfill(EN[key], vars);
  }
  // "<figure> EGP" in the page language (common.money).
  function money(figure) {
    return T('common.money', { amount: figure });
  }
  // Right-to-left only: the figure is isolated (FSI ... PDI) so it cannot be
  // reordered by the Arabic around it. English: untouched.
  function rtlIsolate(str) {
    try {
      if (I18N && typeof I18N.dir === 'function' && I18N.dir() === 'rtl' && typeof I18N.isolate === 'function') {
        return I18N.isolate(str);
      }
    } catch (e) { /* English behaviour */ }
    return str;
  }

  var ROOT_SELECTOR = '#upgrade-finance-slot, [data-ug-finance-root]';
  var GOLD_TOKENS = ['gold', 'rg', 'g'];
  var MONTHLY_ROUND_TO = 5; // nearest 5 EGP -- a display convention only, not a provider's rule

  function egp(n) {
    if (!isFinite(n)) return '—';
    return rtlIsolate(new Intl.NumberFormat('en-EG', { maximumFractionDigits: 0 }).format(Math.max(0, Math.round(n))));
  }

  function isGoldOrUnknown(gradeRaw) {
    var s = String(gradeRaw || '').trim().toLowerCase();
    if (s === '') return true;
    if (GOLD_TOKENS.indexOf(s) !== -1) return true;
    return s.indexOf('gold') !== -1;
  }

  // "[monthly] EGP a month" -> the value; an unknown [name] is left as it is.
  function fill(template, vars) {
    return String(template || '').replace(/\[(\w+)\]/g, function (whole, name) {
      return Object.prototype.hasOwnProperty.call(vars, name) ? vars[name] : whole;
    });
  }

  var MIN_PRICE = 1000; // matches the min= declared on the standalone calculator's input
  // 28 Sep 2026 (D11): was 100,000 -- only 1,201 EGP over the dearest phone on
  // the October list (17 Pro Max 512 Diamond, 98,799, LOG-022). Raised for
  // real headroom, not just today's ceiling.
  //
  // 28 Sep 2026 REVIEW FINDING: this MIN_PRICE/MAX_PRICE pair only gates the
  // "full" mode's manual price input -- fin_mode == 'full' -- and nothing in
  // this theme currently mounts that mode. `{% render 'upgrade-finance',
  // mode: 'full', ... %}` exists only inside snippets/upgrade-finance.liquid's
  // own USAGE comment; /pages/financing runs sections/upgrade-finance-page.
  // liquid, which uses assets/upgrade-calc.js (no price cap of its own), not
  // this file's full mode. The raised headroom is correct and untested by any
  // live page today -- see tests/finance-drop.test.js's own note where this
  // path is exercised structurally, not behaviourally.
  var MAX_PRICE = 150000;

  // 28 Sep 2026 (D11): no cents guess any more -- see the file header. Both
  // real senders of data-price (assets/upgrade-pdp.js's buy-box and this
  // snippet's own `price` parameter) send plain EGP, and the typed-price path
  // (below) never went through this heuristic to begin with.
  function parsePrice(raw) {
    var n = parseFloat(raw);
    if (!isFinite(n) || n <= 0) return null;
    return n;
  }

  function initWidget(root) {
    if (root.__ugFinanceRender) return; // already initialised

    var emptyEl = root.querySelector('[data-fin-empty]');
    var contentEl = root.querySelector('[data-fin-content]');
    var priceOutEls = root.querySelectorAll('[data-fin-price-out]');
    var monthlyEl = root.querySelector('[data-fin-monthly]');
    var totalEl = root.querySelector('[data-fin-total]');
    var extraEl = root.querySelector('[data-fin-extra]');
    var tenorButtons = Array.prototype.slice.call(root.querySelectorAll('[data-fin-tenor]'));
    var rateSelect = root.querySelector('[data-fin-rate]');
    var priceInput = root.querySelector('[data-fin-price-input]');

    // The chip label for n months (upgrade.finance.months), else the number.
    function termLabel(n) {
      for (var t = 0; t < tenorButtons.length; t++) {
        if (parseInt(tenorButtons[t].getAttribute('data-fin-tenor'), 10) === n) {
          var label = String(tenorButtons[t].textContent || '').replace(/\s+/g, ' ').trim();
          if (label) return label;
        }
      }
      return egp(n);
    }

    // The "Pay monthly" dropdown (compact mode only; see the file header).
    var drop = root.querySelector('[data-fin-drop]');
    var summaryEl = root.querySelector('[data-fin-summary]');
    var moreEl = root.querySelector('[data-fin-more]');
    var statusEl = root.querySelector('[data-fin-status]');
    var moreBase = moreEl ? String(moreEl.getAttribute('href') || '').split('?')[0] : '';
    var summaryLine = '';

    // priceInput only exists in the standalone calculator's "full" mode
    // (snippets/upgrade-finance.liquid). That page has no grade/storage
    // selector, so the shared empty-state copy ("Choose a grade and storage
    // above...") does not apply there -- swap in wording that matches what
    // is actually on this page, without touching the markup file.
    if (priceInput && emptyEl) {
      emptyEl.textContent = T('finance.empty_full', { range: money(egp(MIN_PRICE) + '–' + egp(MAX_PRICE)) });
    }

    // Inline validation message for the manual price input, created here
    // rather than in the Liquid markup this file does not own. Hidden by
    // default; only shown when the shopper types something out of bounds.
    var priceErrorEl = null;
    if (priceInput) {
      priceErrorEl = document.createElement('p');
      priceErrorEl.className = 'ug-field__hint ug-field__hint--error';
      priceErrorEl.setAttribute('data-fin-price-error', '');
      priceErrorEl.setAttribute('aria-live', 'polite');
      priceErrorEl.hidden = true;
      priceInput.insertAdjacentElement('afterend', priceErrorEl);
    }

    // 27 Sep 2026 (punch list B11, "Remove the hidden fees"): with no fee
    // selector on the page the fallback was a 5% add-on nobody could see.
    // It is 0 now; any fee in this widget is one the shopper picked.
    var state = { tenor: 3, rate: rateSelect ? (parseFloat(rateSelect.value) || 0) : 0 };

    var preselected = tenorButtons.filter(function (b) {
      return b.getAttribute('aria-checked') === 'true';
    })[0];
    if (preselected) {
      state.tenor = parseInt(preselected.getAttribute('data-fin-tenor'), 10) || 3;
    }

    // LF16 fix (29 Sep 2026, fix_now, audit w4k72rk5w). Tracks the price
    // this widget last rendered against, so render() can tell a genuine
    // grade/storage change (a NEW price) from a re-render at the SAME
    // price (a tenor press, a rate change). `undefined` on the very first
    // call, which is never equal to any real price or to null, so the
    // first render is treated as "new" too -- harmless, statusEl starts
    // empty anyway.
    var lastRenderedPrice;

    function render() {
      // Gate removed: the widget used to show on Gold only, on the reasoning
      // that a subvented offer is unaffordable on thinner grades. Upgrade runs
      // several instalment facilities, so availability is not grade-dependent,
      // and a monthly figure is the number that converts. Kept as a no-op call
      // so the helper and its tokens stay referenced.
      void isGoldOrUnknown(root.getAttribute('data-grade'));
      root.hidden = false;

      var price = parsePrice(root.getAttribute('data-price'));

      // LF16 fix (29 Sep 2026, fix_now, audit w4k72rk5w): a grade/storage
      // change re-renders this widget with a NEW price, but statusEl kept
      // announcing whatever tenor-press figure was last spoken -- a
      // screen-reader user who changed grade heard a monthly figure for
      // the PREVIOUS price with nothing telling them it was stale. The
      // PDP's own live summary (a separate, existing live region) already
      // announces the new price; this region is cleared, not re-filled
      // with a guess, so it cannot go on describing a number that no
      // longer applies. A same-price re-render (a tenor press, a rate
      // change) leaves it alone -- those write their own, current
      // announcement immediately after calling render() (below).
      if (price !== lastRenderedPrice) {
        if (statusEl) { statusEl.textContent = ''; }
        lastRenderedPrice = price;
      }

      if (price === null) {
        if (emptyEl) emptyEl.hidden = false;
        if (contentEl) contentEl.hidden = true;
        summaryLine = '';
        if (summaryEl) summaryEl.textContent = root.getAttribute('data-summary-empty') || summaryEl.textContent;
        if (moreEl && moreBase) moreEl.setAttribute('href', moreBase);
        return;
      }
      if (emptyEl) emptyEl.hidden = true;
      if (contentEl) contentEl.hidden = false;

      for (var i = 0; i < priceOutEls.length; i++) {
        priceOutEls[i].textContent = money(egp(price));
      }

      if (drop) {
        var perMonth = Math.ceil(price / state.tenor);
        // [term]: the chip's own counted label (03-FIXES.md D2); English has no [term].
        summaryLine = fill(root.getAttribute('data-summary-template'), { monthly: egp(perMonth), months: egp(state.tenor), term: termLabel(state.tenor) });
        if (summaryEl) summaryEl.textContent = summaryLine;
        // The How to pay page reads ?price= into its own calculator.
        if (moreEl && moreBase) moreEl.setAttribute('href', moreBase + '?price=' + Math.round(price));
        return;
      }

      var rawMonthly = (price * (1 + state.rate)) / state.tenor;
      var monthly = Math.round(rawMonthly / MONTHLY_ROUND_TO) * MONTHLY_ROUND_TO;
      var total = monthly * state.tenor;
      var extra = total - price;

      if (monthlyEl) monthlyEl.textContent = T('finance.monthly_value', { amount: money(egp(monthly)) });
      if (totalEl) totalEl.textContent = T('finance.total_value', { amount: money(egp(total)) });
      if (extraEl) extraEl.textContent = T('finance.extra_value', { amount: money(extra <= 0 ? egp(0) : egp(extra)) });
    }

    tenorButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        tenorButtons.forEach(function (b) {
          b.setAttribute('aria-checked', 'false');
          b.classList.remove('is-selected');
        });
        btn.setAttribute('aria-checked', 'true');
        btn.classList.add('is-selected');
        state.tenor = parseInt(btn.getAttribute('data-fin-tenor'), 10) || state.tenor;
        render();
        // The figure lives in the summary row, which a screen reader does not
        // re-read on its own; say the new one.
        if (statusEl && summaryLine) statusEl.textContent = T('finance.status', { summary: summaryLine });
      });
    });

    if (rateSelect) {
      rateSelect.addEventListener('change', function () {
        state.rate = parseFloat(rateSelect.value) || 0;
        render();
      });
    }

    if (priceInput) {
      priceInput.addEventListener('input', function () {
        var raw = priceInput.value;

        function setError(msg) {
          if (!priceErrorEl) return;
          if (msg) {
            priceErrorEl.textContent = msg;
            priceErrorEl.hidden = false;
          } else {
            priceErrorEl.textContent = '';
            priceErrorEl.hidden = true;
          }
        }

        if (raw === '') {
          // Shopper cleared the field -- no error, just no price yet.
          setError(null);
          root.setAttribute('data-price', '');
          return;
        }

        var v = parseFloat(raw);

        // No cents heuristic here: this value came straight from a human,
        // never from the data-price attribute contract, so it is taken
        // exactly as typed and only ever validated, never silently rescaled.
        if (!isFinite(v) || v <= 0) {
          setError(T('finance.error_not_positive'));
          root.setAttribute('data-price', '');
          return;
        }
        if (v < MIN_PRICE) {
          setError(T('finance.error_min', { amount: money(egp(MIN_PRICE)) }));
          root.setAttribute('data-price', '');
          return;
        }
        if (v > MAX_PRICE) {
          setError(T('finance.error_max', { amount: money(egp(MAX_PRICE)) }));
          root.setAttribute('data-price', '');
          return;
        }

        setError(null);
        root.setAttribute('data-price', String(v));
      });
    }

    var observer = new MutationObserver(function (mutations) {
      var relevant = mutations.some(function (m) {
        return m.attributeName === 'data-price' || m.attributeName === 'data-grade';
      });
      if (relevant) render();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-price', 'data-grade'] });

    // "See ways to pay" beside the price points here; land on it open.
    if (drop && root.id) {
      document.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a[href="#' + root.id + '"]') : null;
        if (a) drop.open = true;
      });
    }

    root.__ugFinanceRender = render;
    render();
  }

  function boot() {
    var roots = document.querySelectorAll(ROOT_SELECTOR);
    Array.prototype.forEach.call(roots, initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.UpgradeFinance = {
    refresh: function (idOrEl) {
      var el = typeof idOrEl === 'string' ? document.getElementById(idOrEl) : idOrEl;
      if (!el) return;
      if (!el.__ugFinanceRender) initWidget(el);
      if (el.__ugFinanceRender) el.__ugFinanceRender();
    }
  };
})();
